import React, { useEffect, useMemo, useState } from "react";
import { limit, query } from "firebase/firestore";
import { Alert, Button, InputAdornment, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
  SEARCH_LIMIT,
  customerCountQuery,
  customerSearchQuery,
  customersQuery,
} from "../../lib/queries";
import { matchesAllWords, primaryToken, searchWords } from "../../lib/search";
import {
  describeQueryError,
  useCounts,
  usePagedQuery,
} from "../../hooks/usePaged";
import { useLiveQuery } from "../../hooks/useLive";
import { Loading, PageHeader } from "../../components/ui";
import CustomerDialog from "../../components/CustomerDialog";
import CustomerTable from "../../components/CustomerTable";
import { pagingProps } from "../../components/LicenseTable";
import IssueKeyDialog from "../../components/IssueKeyDialog";
import { useDistributorData } from "./DistributorData";

const PAGE_SIZE = 25;

export default function CustomersPage() {
  const { distributorId, distributor } = useDistributorData();
  const [input, setInput] = useState("");
  const [term, setTerm] = useState("");
  const [openId, setOpenId] = useState(null); // customer id | "new" | null
  const [issueFor, setIssueFor] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(input), 300);
    return () => clearTimeout(timer);
  }, [input]);

  const words = searchWords(term);
  const token = primaryToken(words);
  const searching = !!token;
  const total = useCounts(`mycustomers|${distributorId}|${refresh}`, {
    all: () => customerCountQuery(distributorId),
  });
  const paged = usePagedQuery(
    searching ? null : `mycustomers|${distributorId}`,
    () => customersQuery(distributorId),
    {
      pageSize: PAGE_SIZE,
    },
  );
  const found = useLiveQuery(
    searching ? `mycustomers|${distributorId}|${token}` : null,
    () => query(customerSearchQuery(distributorId, token), limit(SEARCH_LIMIT)),
  );
  const searchRows = useMemo(
    () => found.data.filter((c) => matchesAllWords(c, words)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [found.data, words.join()],
  );
  const rows = searching ? searchRows : paged.rows;
  const error = searching ? found.error : paged.error;
  const open =
    openId && openId !== "new" ? rows.find((c) => c.id === openId) : null;
  const canIssue = distributor?.active !== false;

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Optional: keep basic details of the people you issue keys to, so you can see which keys belong to whom."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenId("new")}
          >
            Add customer
          </Button>
        }
      />
      <TextField
        placeholder="Search name, email, phone, city…"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        sx={{ mb: 2.5, width: { xs: "100%", sm: 360 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {describeQueryError(error)}
        </Alert>
      )}
      {(searching ? found.loading : paged.loading) && !rows.length ? (
        <Loading />
      ) : (
        <CustomerTable
          rows={rows}
          onRowClick={(c) => setOpenId(c.id)}
          resetKey={token}
          paging={
            searching
              ? undefined
              : pagingProps(paged, PAGE_SIZE, total.counts.all)
          }
          emptyText={
            searching
              ? "No customers match this search."
              : "No customers yet. Add one here, or tick “Save to my customers” when you issue a key."
          }
        />
      )}

      <CustomerDialog
        open={!!openId && (openId === "new" || !!open)}
        customer={open}
        distributorId={distributorId}
        onIssueKey={canIssue ? (c) => setIssueFor(c) : undefined}
        onClose={() => {
          setOpenId(null);
          setRefresh((n) => n + 1);
        }}
      />
      {distributor && (
        <IssueKeyDialog
          open={!!issueFor}
          distributor={distributor}
          initialCustomer={issueFor}
          onClose={() => setIssueFor(null)}
        />
      )}
    </>
  );
}
